import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";

const PRODUCTS_COLLECTION = "products";

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function mapProduct(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    name: data.name || "",
    description: data.description || "",
    price: toNumber(data.price, 0),
    image_url: data.image_url || "",
    category: data.category || "adult",
    embroidery_detail: data.embroidery_detail || "",
    stock: toNumber(data.stock, 0),
    featured: Boolean(data.featured),
    gallery: Array.isArray(data.gallery) ? data.gallery : [],
  };
}

function productPayload(product) {
  return {
    name: (product.name || "").trim(),
    description: (product.description || "").trim(),
    price: toNumber(product.price, 0),
    image_url: (product.image_url || "").trim(),
    category: product.category || "adult",
    embroidery_detail: (product.embroidery_detail || "").trim(),
    stock: toNumber(product.stock, 0),
    featured: Boolean(product.featured),
    gallery: Array.isArray(product.gallery) ? product.gallery : [],
    updated_at: serverTimestamp(),
  };
}

export function subscribeProducts(callback, onError) {
  const q = query(collection(db, PRODUCTS_COLLECTION), orderBy("name", "asc"));
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map(mapProduct)),
    (error) => {
      if (typeof onError === "function") onError(error);
    }
  );
}

export async function getFeaturedProducts(max = 4) {
  const q = query(
    collection(db, PRODUCTS_COLLECTION),
    where("featured", "==", true),
    limit(max)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(mapProduct);
}

export async function getProductById(id) {
  const productRef = doc(db, PRODUCTS_COLLECTION, id);
  const productSnap = await getDoc(productRef);
  if (!productSnap.exists()) return null;
  return mapProduct(productSnap);
}

export async function createProduct(product) {
  const payload = {
    ...productPayload(product),
    created_at: serverTimestamp(),
  };
  const created = await addDoc(collection(db, PRODUCTS_COLLECTION), payload);
  return created.id;
}

export async function updateProduct(id, product) {
  const payload = productPayload(product);
  await updateDoc(doc(db, PRODUCTS_COLLECTION, id), payload);
}

export async function removeProduct(id) {
  await deleteDoc(doc(db, PRODUCTS_COLLECTION, id));
}

export async function addImageToGallery(productId, imageUrl) {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  const productSnap = await getDoc(productRef);
  if (!productSnap.exists()) throw new Error("Product not found");
  
  const currentGallery = Array.isArray(productSnap.data().gallery) ? productSnap.data().gallery : [];
  const updatedGallery = [...currentGallery, imageUrl];
  
  await updateDoc(productRef, { gallery: updatedGallery, updated_at: serverTimestamp() });
  return updatedGallery;
}

export async function removeImageFromGallery(productId, imageUrl) {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  const productSnap = await getDoc(productRef);
  if (!productSnap.exists()) throw new Error("Product not found");
  
  const currentGallery = Array.isArray(productSnap.data().gallery) ? productSnap.data().gallery : [];
  const updatedGallery = currentGallery.filter((url) => url !== imageUrl);
  
  await updateDoc(productRef, { gallery: updatedGallery, updated_at: serverTimestamp() });
  return updatedGallery;
}

export async function reorderGallery(productId, newOrder) {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  await updateDoc(productRef, { gallery: newOrder, updated_at: serverTimestamp() });
}
